/**
 * O avatar ANIMADO: malha com esqueleto, um mixer por jogador. three.js PURO
 * (`ADR-0003`) — NEVER importe react aqui.
 *
 * Por que é um arquivo separado de `avatar.ts` em vez de um `if` dentro dele: o
 * avatar estático é uma GEOMETRIA compartilhada pela sala inteira, e o animado
 * NEVER pode ser — cada jogador tem a própria pose, então cada um tem o próprio
 * esqueleto e o próprio mixer. São duas formas de vida diferentes na cena, e
 * misturá-las num arquivo só esconderia justamente essa diferença.
 *
 * O `.glb` chega com altura 1 e pés em `y = 0` (`scripts/lib/rig-fbx.ts`). Quem
 * multiplica pela altura de design é aqui, e é de propósito: token muda sem
 * avisar, e asset NEVER se regrava por causa disso.
 */
import { tokens } from '@game/design';
import { AnimationMixer, type AnimationClip, type Material, type Object3D } from 'three';

/**
 * Acima de que velocidade o robô troca de animação, em unidades por segundo.
 *
 * NEVER saem de gosto: `balance.player.speed` é a velocidade de andar do jogo, e
 * o que se quer é que a passada CASE com o deslocamento — abaixo de um terço
 * dela o robô está parando, e acima de dois terços já é corrida. O sintoma de
 * errar isto é o pé patinando no chão, e ele aparece na hora.
 */
export interface LocomotionThresholds {
  readonly walk: number;
  readonly run: number;
}

/** Tempo de mistura entre duas animações. Curto: troca dura é o que lê como robô travando. */
const CROSSFADE_S = 0.18;

type Locomotion = 'idle' | 'walking' | 'running';

export interface AvatarInstance {
  /** O que entra na cena. Posição e rotação são escritas pelo `World`. */
  readonly object: Object3D;
  /** Papel do jogador (você, outro, caído) — quem decide continua sendo o `World`. */
  setMaterial(material: Material): void;
  /** `speed` em unidades por segundo, derivada do movimento AUTORITATIVO. */
  update(deltaSeconds: number, speed: number): void;
  dispose(): void;
}

export interface AvatarRig {
  create(): AvatarInstance;
  /** Solta o que é compartilhado entre todas as instâncias. */
  dispose(): void;
}

class RigInstance implements AvatarInstance {
  private readonly mixer: AnimationMixer;
  private current: Locomotion = 'idle';

  constructor(
    readonly object: Object3D,
    private readonly clips: Readonly<Record<Locomotion, AnimationClip>>,
    private readonly thresholds: LocomotionThresholds,
  ) {
    this.mixer = new AnimationMixer(object);
    this.mixer.clipAction(clips.idle).play();
  }

  setMaterial(material: Material): void {
    this.object.traverse((node) => {
      const mesh = node as { isMesh?: boolean; material?: Material };
      if (mesh.isMesh === true) mesh.material = material;
    });
  }

  update(deltaSeconds: number, speed: number): void {
    const wanted = this.locomotionFor(speed);
    if (wanted !== this.current) {
      const from = this.mixer.clipAction(this.clips[this.current]);
      const to = this.mixer.clipAction(this.clips[wanted]);
      // `reset()` antes do fade: sem ele a animação recomeça de onde parou da
      // última vez que foi usada, e o robô entra no passo com a perna no ar.
      to.reset().play();
      to.crossFadeFrom(from, CROSSFADE_S, false);
      this.current = wanted;
    }
    if (deltaSeconds > 0) this.mixer.update(deltaSeconds);
  }

  dispose(): void {
    this.mixer.stopAllAction();
    for (const clip of Object.values(this.clips)) this.mixer.uncacheClip(clip);
    this.mixer.uncacheRoot(this.object);
  }

  private locomotionFor(speed: number): Locomotion {
    if (speed >= this.thresholds.run) return 'running';
    if (speed >= this.thresholds.walk) return 'walking';
    return 'idle';
  }
}

/** Os clipes que a locomoção exige. Faltar um NEVER é degradação: é asset errado. */
function requireClips(animations: readonly AnimationClip[]): Record<Locomotion, AnimationClip> {
  const byName = new Map(animations.map((clip) => [clip.name, clip]));
  const found: Partial<Record<Locomotion, AnimationClip>> = {};
  for (const name of ['idle', 'walking', 'running'] as const) {
    const clip = byName.get(name);
    if (clip === undefined) throw new Error(`o .glb do avatar NEVER tem a animação "${name}"`);
    found[name] = clip;
  }
  return found as Record<Locomotion, AnimationClip>;
}

/**
 * Busca o `.glb` animado e devolve a fábrica de avatares.
 *
 * `GLTFLoader` e `SkeletonUtils` entram por `import()` dinâmico pela mesma razão
 * do avatar estático: quem está com a flag desligada NEVER deve baixar o parser.
 */
export async function loadAvatarRig(
  url: string,
  thresholds: LocomotionThresholds,
): Promise<AvatarRig> {
  const [{ GLTFLoader }, skeletonUtils] = await Promise.all([
    import('three/examples/jsm/loaders/GLTFLoader.js'),
    import('three/examples/jsm/utils/SkeletonUtils.js'),
  ]);
  const gltf = await new GLTFLoader().loadAsync(url);
  const clips = requireClips(gltf.animations);

  // A escala mora no nó de origem, e cada clone a herda. Fazer aqui é fazer UMA
  // vez para a sala inteira.
  gltf.scene.scale.setScalar(tokens.world.avatarHeight);

  return {
    create(): AvatarInstance {
      // Clone que ACOMPANHA o esqueleto. `Object3D.clone()` NEVER serve: ele
      // copia a malha e deixa os ossos apontando para o esqueleto do original,
      // e o sintoma é todo mundo da sala fazendo exatamente a mesma pose.
      const object = skeletonUtils.clone(gltf.scene);
      return new RigInstance(object, clips, thresholds);
    },
    dispose(): void {
      gltf.scene.traverse((node) => {
        const mesh = node as { isMesh?: boolean; geometry?: { dispose: () => void } };
        if (mesh.isMesh === true) mesh.geometry?.dispose();
      });
    },
  };
}
